import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../utils/roles';

const formatMoney = (n) => `KES ${Number(n || 0).toLocaleString('en-KE')}`;

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FailedCheckoutsPage = () => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [events, setEvents] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventId, setEventId] = useState(searchParams.get('eventId') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalAttempts: 0
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await adminAPI.getEvents({ limit: 100, includeDisabled: 'true' });
        const list = res?.data?.events || [];
        setEvents(list);
        if (!isAdmin && !eventId && list.length === 1) {
          setEventId(list[0].id);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load events');
      }
    };
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const syncParams = useCallback(() => {
    const next = new URLSearchParams();
    if (eventId) next.set('eventId', eventId);
    if (debouncedSearch) next.set('search', debouncedSearch);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [eventId, debouncedSearch, page, setSearchParams]);

  useEffect(() => {
    syncParams();
  }, [syncParams]);

  const loadAttempts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (eventId) params.eventId = eventId;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await adminAPI.getFailedCheckouts(params);
      if (res?.success) {
        setAttempts(res.data.attempts || []);
        setPagination(res.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalAttempts: 0
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [page, eventId, debouncedSearch]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  useEffect(() => {
    setPage(1);
  }, [eventId, debouncedSearch]);

  const buyerName = (row) =>
    [row.buyerFirstName, row.buyerLastName].filter(Boolean).join(' ') || '—';

  const cartLabel = (row) => {
    if (Array.isArray(row.cartItems) && row.cartItems.length) {
      return row.cartItems
        .map((item) => `${item.quantity || 1}× ${item.name || item.tierName || 'Ticket'}`)
        .join(', ');
    }
    return `${row.quantity || 1}× ${row.ticketType || 'Ticket'}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 ml-0 lg:ml-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 p-4 sm:p-6 space-y-4 w-full overflow-x-hidden">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Follow-ups</h1>
            <p className="text-sm text-gray-600 mt-1">
              Failed checkouts that still need customer care. Rows drop off automatically after that buyer pays successfully.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link
              to="/admin/sales"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-gray-600 hover:border-orange-300"
            >
              Sales / Tickets
            </Link>
            <span className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 font-semibold text-orange-800">
              Failed attempts
            </span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block text-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Event</span>
                <select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="">{isAdmin ? 'All events' : 'Select event'}</option>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.title}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm sm:col-span-2 lg:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Search</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, phone, email, bill ref, reason…"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </label>
            </div>
            <p className="mt-3 text-xs text-gray-500 leading-relaxed">
              These are M-Pesa failures with no later successful payment for the same buyer on that event.
              Use the phone or email to call / message them. Once they buy successfully, the row disappears.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                {pagination.totalAttempts} follow-up{pagination.totalAttempts !== 1 ? 's' : ''}
              </p>
              <button
                type="button"
                onClick={loadAttempts}
                className="text-sm font-medium text-orange-700 hover:underline"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-500">Loading failed attempts…</div>
            ) : attempts.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No unresolved failed checkouts. Great — nothing to chase right now.
              </div>
            ) : (
              <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Event / order</th>
                        <th className="px-4 py-3">Failure</th>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3 text-right">Contact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {attempts.map((row) => (
                        <tr key={row.id} className="hover:bg-orange-50/40">
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-gray-900">{buyerName(row)}</div>
                            <div className="text-xs text-gray-600 mt-0.5">{row.phoneNumber || '—'}</div>
                            <div className="text-xs text-gray-500">{row.buyerEmail || '—'}</div>
                            {row.attemptCount > 1 && (
                              <span className="mt-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                {row.attemptCount} failed attempts
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-900">{row.event?.title || '—'}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{cartLabel(row)}</div>
                            <div className="text-xs font-semibold text-gray-800 mt-1">{formatMoney(row.amount)}</div>
                            {row.billRef && (
                              <div className="text-[11px] font-mono text-orange-700 mt-0.5">Ref: {row.billRef}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800">
                              Failed
                            </span>
                            <p className="mt-1.5 text-xs text-red-800/90 max-w-xs leading-snug">
                              {row.failureReason || 'Payment was not completed'}
                            </p>
                          </td>
                          <td className="px-4 py-3 align-top text-xs text-gray-600 whitespace-nowrap">
                            {formatDateTime(row.failedAt)}
                          </td>
                          <td className="px-4 py-3 align-top text-right space-y-1.5">
                            {row.phoneNumber && (
                              <a
                                href={`tel:${row.phoneNumber}`}
                                className="block text-sm font-semibold text-orange-700 hover:underline"
                              >
                                Call
                              </a>
                            )}
                            {row.phoneNumber && (
                              <a
                                href={`https://wa.me/${String(row.phoneNumber).replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="block text-sm font-semibold text-emerald-700 hover:underline"
                              >
                                WhatsApp
                              </a>
                            )}
                            {row.buyerEmail && (
                              <a
                                href={`mailto:${row.buyerEmail}?subject=${encodeURIComponent(`Help with your ${row.event?.title || 'ticket'} checkout`)}`}
                                className="block text-sm font-semibold text-gray-700 hover:underline"
                              >
                                Email
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden divide-y divide-gray-100">
                  {attempts.map((row) => (
                    <div key={row.id} className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">{buyerName(row)}</p>
                          <p className="text-xs text-gray-500">{row.event?.title}</p>
                        </div>
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-800">
                          Failed
                        </span>
                      </div>
                      <p className="text-sm text-gray-800">{formatMoney(row.amount)} · {cartLabel(row)}</p>
                      <p className="text-xs text-red-800">{row.failureReason || 'Payment was not completed'}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(row.failedAt)}</p>
                      <div className="flex flex-wrap gap-3 pt-1">
                        {row.phoneNumber && (
                          <a href={`tel:${row.phoneNumber}`} className="text-sm font-semibold text-orange-700">Call</a>
                        )}
                        {row.phoneNumber && (
                          <a
                            href={`https://wa.me/${String(row.phoneNumber).replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-semibold text-emerald-700"
                          >
                            WhatsApp
                          </a>
                        )}
                        {row.buyerEmail && (
                          <a href={`mailto:${row.buyerEmail}`} className="text-sm font-semibold text-gray-700">Email</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {pagination.totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="text-sm font-medium text-orange-700 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= pagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="text-sm font-medium text-orange-700 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default FailedCheckoutsPage;
